import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, GraduationCap } from "lucide-react";
import { PageHero, Section, Card } from "@/components/Section";
import { getFormations } from "@/lib/api";
import { IMAGES } from "@/lib/site";

export default function Formations() {
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getFormations()
      .then(setFormations)
      .catch(() => setFormations([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="formations-page">
      <PageHero
        eyebrow="Nos formations"
        title="Des filières professionnelles concrètes, du CAP au Bac Pro"
        subtitle="Chaque formation est pensée pour donner aux jeunes les compétences techniques et humaines nécessaires à leur insertion professionnelle ou à la poursuite d'études."
        image={IMAGES.workshop}
      />

      <Section
        eyebrow="Catalogue"
        title="Choisir sa voie au lycée Joseph Wresinski"
      >
        {loading ? (
          <p className="text-muted-foreground">Chargement des formations…</p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {formations.map((f, i) => (
              <Card key={f.slug} data-testid={`formation-${f.slug}`}>
                <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-wider text-brand-sand-dark">
                  <span className="rounded-full bg-brand-sand/30 px-3 py-1 text-brand-slate-dark">
                    {f.niveau}
                  </span>
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" />
                    {f.duree}
                  </span>
                </div>
                <h3 className="mt-5 font-heading text-2xl font-bold leading-tight text-brand-slate-dark">
                  {f.nom}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {f.resume}
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {f.debouches.slice(0, 3).map((d) => (
                    <span
                      key={d}
                      className="rounded-full bg-brand-sage/15 px-3 py-1 text-xs font-medium text-brand-sage-dark"
                    >
                      {d}
                    </span>
                  ))}
                </div>
                <Link
                  to={`/formations/${f.slug}`}
                  data-testid={`formation-link-${f.slug}`}
                  className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-brand-slate transition-colors hover:text-brand-sand-dark"
                >
                  Découvrir la formation
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Card>
            ))}
          </div>
        )}
      </Section>

      <Section wrapper="bg-brand-sand-wrapper/50">
        <div className="grid items-center gap-10 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-brand-slate-dark sm:text-4xl">
              Une question sur nos formations ?
            </h2>
            <p className="mt-4 max-w-xl text-base text-muted-foreground">
              Notre équipe d'orientation est disponible pour échanger avec vous
              et votre enfant sur le parcours le plus adapté.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link
              to="/contact"
              data-testid="formations-cta-contact"
              className="inline-flex items-center gap-2 rounded-full bg-brand-slate px-6 py-3.5 text-sm font-semibold text-brand-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-slate-dark"
            >
              <GraduationCap className="h-4 w-4" />
              Demander un rendez-vous
            </Link>
            <Link
              to="/telechargements"
              data-testid="formations-cta-download"
              className="inline-flex items-center gap-2 rounded-full border border-brand-slate/20 px-6 py-3.5 text-sm font-semibold text-brand-slate-dark transition-all duration-300 hover:bg-brand-slate hover:text-brand-cream"
            >
              Télécharger la plaquette
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
